import { useEffect, useRef, useState } from 'react'

/**
 *
 * **useEventListener** is a general purpose React hook that helps with managing the addition and removal
 *  of event listeners to event targets such as HTMLElements or window
 *
 * @param eventType
 *  a valid event type
 * @param eventListener
 *  a valid event listener or null
 * @param eventTarget
 *  an optional valid event target or null (defaults to `window` if undefined)
 */
function useEventListener<T extends string = string>(
  eventType: T,
  eventListener: EventListener | null,
  eventTarget: EventTarget | null = window,
) {
  // Note: we're adding this indirection of using a ref that stores the event listening callback
  //  to avoid unnecessary adding & removing of listeners from the dom if it's just the callback
  //  that's changing (The listeners should only directly be replaced if the eventType or eventTarget changes)
  const eventListenerRef = useRef<EventListener | null>(null)
  const [hasEventListener, setHasEventListener] = useState(!!eventListener)
  useEffect(() => {
    setHasEventListener(!!eventListener)
    eventListenerRef.current = eventListener
  }, [eventListener])

  useEffect(() => {
    const currentEventListener = eventListenerRef.current
    if (!eventTarget?.addEventListener || !currentEventListener) {
      return
    }

    const listener = (event: Event) => currentEventListener(event)

    eventTarget.addEventListener(eventType, listener)

    return () => {
      eventTarget.removeEventListener(eventType, listener)
    }
  }, [eventType, eventTarget, hasEventListener])
}

export default useEventListener
